import { useState, useEffect, useMemo, useCallback } from 'react'
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Modal, TextInput, FlatList, Pressable, Switch } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { Card, SubHeader, Msg } from '../../components/Leagues/ui'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import GradientButton from '../../components/GradientButton'
import { BibTag } from '../../components/Pick/ui'
import Sheet from './Sheet'

// Correzione Pick (admin di lega): una chiamata sola per caricare (fn_admin_pick_grid) e una per salvare (fn_admin_set_picks):
// si scrivono SOLO gli slot cambiati, con errori chiari, avviso ai giocatori e "Annulla" (sql/20261029100000_correzione_pick.sql).
// La scelta del corridore e' in un elenco con ricerca (nome o dorsale): niente piu' un pulsante per ogni corridore.

function RiderPicker({ riders, onSelect, onClose }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const [q, setQ] = useState('')
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return riders
    return riders.filter(r => r.name.toLowerCase().includes(needle) || (/^\d+$/.test(needle) && String(r.bib ?? '').startsWith(needle)))
  }, [q, riders])
  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' }} onPress={onClose}>
        <Pressable style={{ backgroundColor: tc.bgCard, borderTopLeftRadius: 18, borderTopRightRadius: 18, maxHeight: '80%', paddingTop: 12 }} onPress={() => {}}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingBottom: 8 }}>
            <TextInput
              value={q} onChangeText={setQ} autoFocus placeholder={t('league.pick_admin_search')} placeholderTextColor={tc.textMuted}
              style={{ flex: 1, minHeight: 44, borderRadius: 10, borderWidth: 1, borderColor: tc.border, backgroundColor: tc.bgCard2, color: tc.text, paddingHorizontal: 12, fontSize: 15 }}
            />
            <TouchableOpacity onPress={onClose} style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={22} color={tc.text} />
            </TouchableOpacity>
          </View>
          <FlatList
            data={list}
            keyExtractor={r => String(r.id_rider)}
            keyboardShouldPersistTaps="handled"
            initialNumToRender={20}
            renderItem={({ item: r }) => (
              <TouchableOpacity onPress={() => onSelect(r.id_rider)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 48, paddingHorizontal: 14, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: tc.border }} accessibilityRole="button">
                <BibTag bib={r.bib} size="sm" />
                <Text style={{ flex: 1, fontSize: 14, color: tc.text, opacity: r.is_dnf ? 0.55 : 1 }} numberOfLines={1}>{r.name}{r.team ? `  [${r.team}]` : ''}</Text>
                {r.is_dnf ? <Text style={{ fontSize: 11, color: tc.textMuted }}>{t('league.pick_admin_retired')}</Text> : null}
              </TouchableOpacity>
            )}
          />
        </Pressable>
      </Pressable>
    </Modal>
  )
}

export default function SezionePickAdmin({ leagueId, isAdmin }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const [open, setOpen] = useState(false)
  const [races, setRaces] = useState([])
  const [tappe, setTappe] = useState([])
  const [selRace, setSelRace] = useState('')
  const [selStage, setSelStage] = useState('')
  const [utenti, setUtenti] = useState([])
  const [riders, setRiders] = useState([])
  const [grid, setGrid] = useState({})
  const [initial, setInitial] = useState({})
  const [meta, setMeta] = useState({ slots: 1, in_calendar: true })
  const [picker, setPicker] = useState(null)        // { userId, slot }
  const [notify, setNotify] = useState(true)
  const [log, setLog] = useState([])
  const [future, setFuture] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  // gare del calendario di QUESTA lega (non tutte quelle della competizione)
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    supabase.rpc('fn_league_races', { p_id_league: leagueId }).then(({ data }) => {
      if (cancelled) return
      const list = [...(data ?? [])].sort((a, b) => String(b.race_date).localeCompare(String(a.race_date)))
      setRaces(list)
      if (list.length) setSelRace(String(list[0].id_race))
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [open, leagueId])

  useEffect(() => {
    if (!selRace) return
    let cancelled = false
    supabase.from('dim_race_stage').select('id_stage, id_stage_number').eq('id_race', selRace).order('id_stage_number')
      .then(({ data }) => { if (cancelled) return; setTappe(data ?? []); setSelStage(data?.length ? String(data[0].id_stage) : '') })
    return () => { cancelled = true }
  }, [selRace])

  const loadGrid = useCallback(async () => {
    if (!selStage) return
    setLoading(true)
    const { data, error } = await supabase.rpc('fn_admin_pick_grid', { p_id_league: leagueId, p_id_stage: parseInt(selStage) })
    if (error) { setMsg({ type: 'error', text: error.message }); setLoading(false); return }
    setMeta({ slots: data.slots, in_calendar: data.in_calendar })
    setUtenti(data.members ?? []); setRiders(data.riders ?? [])
    const map = {}
    ;(data.picks ?? []).forEach(p => { map[`${p.id_user}_${p.id_slot}`] = p.id_rider })
    setGrid(map); setInitial({ ...map })
    const lg = await supabase.rpc('fn_pick_correction_log', { p_id_league: leagueId, p_id_stage: parseInt(selStage), p_limit: 1 })
    setLog(lg.error ? [] : (lg.data ?? []))
    setLoading(false)
  }, [selStage, leagueId])
  useEffect(() => { if (open) { setFuture([]); loadGrid() } }, [open, loadGrid])

  const riderById = useMemo(() => Object.fromEntries(riders.map(r => [r.id_rider, r])), [riders])
  const slots = Array.from({ length: meta.slots }, (_, i) => i + 1)

  const handleSave = async () => {
    const set = [], clear = []
    for (const u of utenti) {
      for (const slot of slots) {
        const k = `${u.id_user}_${slot}`, now = grid[k] ?? null, was = initial[k] ?? null
        if (now && now !== was) set.push({ id_user: u.id_user, id_slot: slot, id_rider: now })
        else if (!now && was) clear.push({ id_user: u.id_user, id_slot: slot })
      }
    }
    if (!set.length && !clear.length) { setMsg({ type: 'info', text: t('league.pick_admin_nochange') }); return }
    setSaving(true); setMsg(null); setFuture([])
    try {
      const { data, error } = await supabase.rpc('fn_admin_set_picks', { p_id_league: leagueId, p_id_stage: parseInt(selStage), p_set: set, p_clear: clear, p_notify: notify })
      if (error) throw error
      setFuture(data.future_tolte ?? [])
      setMsg({ type: 'success', text: t('league.pick_admin_saved2', { set: data.impostate, removed: data.rimosse }) })
      await loadGrid()
    } catch (e) { setMsg({ type: 'error', text: e.message }) } finally { setSaving(false) }
  }

  const handleUndo = async (idBatch) => {
    setSaving(true); setMsg(null); setFuture([])
    try {
      const { error } = await supabase.rpc('undo_pick_correction', { p_batch: idBatch })
      if (error) throw error
      setMsg({ type: 'success', text: t('league.pick_admin_undone') })
      await loadGrid()
    } catch (e) { setMsg({ type: 'error', text: e.message }) } finally { setSaving(false) }
  }

  if (!isAdmin) return null
  const last = log[0]

  return (
    <>
      <Card>
        <View style={styles.cardRow}>
          <View style={{ flex: 1 }}>
            <SubHeader icon={<MaterialCommunityIcons name="lightning-bolt" size={16} color={tc.rosaText} />}>{t('league.pick_admin_title')}</SubHeader>
            <Text style={[styles.cardDesc, { color: tc.textMuted }]}>{t('league.pick_admin_desc')}</Text>
          </View>
          <GradientButton onPress={() => setOpen(true)} size="sm">{t('league.pick_admin_open')}</GradientButton>
        </View>
      </Card>
      <Sheet visible={open} title={t('league.pick_admin_title')} onClose={() => setOpen(false)}
        footer={<GradientButton onPress={handleSave} loading={saving} disabled={!meta.in_calendar} style={{ width: '100%' }}>{t('league.pick_admin_save')}</GradientButton>}
      >
        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 16 }}>
          <View style={[styles.pickerWrap, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {races.map(g => (
                <TouchableOpacity key={g.id_race} onPress={() => setSelRace(String(g.id_race))} accessibilityRole="button" accessibilityLabel={g.name} accessibilityState={{ selected: selRace === String(g.id_race) }}
                  style={[styles.pickerItem, { backgroundColor: selRace === String(g.id_race) ? tc.rosa : 'transparent' }]}>
                  <Text style={[styles.pickerItemText, { color: selRace === String(g.id_race) ? tc.brandText : tc.textMuted }]} numberOfLines={1}>{g.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {tappe.map(tappa => (
            <TouchableOpacity key={tappa.id_stage} onPress={() => setSelStage(String(tappa.id_stage))} accessibilityRole="button" accessibilityLabel={`T${tappa.id_stage_number}`} accessibilityState={{ selected: selStage === String(tappa.id_stage) }}
              style={[styles.stageBtn, { backgroundColor: selStage === String(tappa.id_stage) ? tc.rosa : tc.bgCard2, borderColor: selStage === String(tappa.id_stage) ? tc.rosa : tc.border }]}>
              <Text style={[styles.stageBtnText, { color: selStage === String(tappa.id_stage) ? tc.brandText : tc.textMuted }]}>T{tappa.id_stage_number}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <Text style={{ fontSize: 13, color: tc.text }}>{t('league.pick_admin_notify')}</Text>
          <Switch value={notify} onValueChange={setNotify} trackColor={{ true: tc.rosa }} />
        </View>
        {msg && <Msg type={msg.type} text={msg.text} />}
        {future.length > 0 && <Msg type="info" text={t('league.pick_admin_future', { n: future.length })} />}
        {last?.can_undo && (
          <TouchableOpacity onPress={() => handleUndo(last.id_batch)} disabled={saving} style={{ alignSelf: 'flex-start', minHeight: 44, justifyContent: 'center', marginBottom: 8 }} accessibilityRole="button">
            <Text style={{ fontSize: 13, color: tc.rosaText, fontFamily: fonts.condensedBold }}>↩ {t('league.pick_admin_undo')}</Text>
          </TouchableOpacity>
        )}
        {!meta.in_calendar && <Msg type="error" text={t('league.pick_admin_no_calendar')} />}
        {loading ? <Text style={{ color: tc.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>{t('league.loading')}</Text> : (
          <View style={{ gap: 12 }}>
            {utenti.map(u => (
              <View key={u.id_user} style={[styles.adminUserCard, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
                <Text style={styles.adminUserName}>{u.display_name}</Text>
                {slots.map(slot => {
                  const k = `${u.id_user}_${slot}`
                  const r = riderById[grid[k]]
                  const changed = (grid[k] ?? null) !== (initial[k] ?? null)
                  return (
                    <View key={slot} style={styles.adminSlotRow}>
                      <Text style={styles.adminSlotNum}>{slot}</Text>
                      <TouchableOpacity onPress={() => setPicker({ userId: u.id_user, slot })} accessibilityRole="button" accessibilityLabel={r ? r.name : t('league.pick_admin_none')}
                        style={[styles.slotBtn, { backgroundColor: changed ? tc.rosaSubtle : tc.bgCard, borderColor: changed ? tc.rosaBorder : tc.border }]}>
                        {r ? <BibTag bib={r.bib} size="sm" /> : null}
                        <Text style={{ flex: 1, fontSize: 13, color: r ? tc.text : tc.textMuted }} numberOfLines={1}>{r ? `${r.name}${r.team ? `  [${r.team}]` : ''}` : t('league.pick_admin_none')}</Text>
                        {r?.is_dnf ? <Text style={{ fontSize: 11, color: tc.textMuted }}>{t('league.pick_admin_retired')}</Text> : null}
                      </TouchableOpacity>
                      {r ? (
                        <TouchableOpacity onPress={() => setGrid(prev => ({ ...prev, [k]: null }))} style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }} accessibilityRole="button" accessibilityLabel={t('league.pick_admin_none')}>
                          <MaterialCommunityIcons name="close-circle-outline" size={20} color={tc.textMuted} />
                        </TouchableOpacity>
                      ) : <View style={{ width: 44 }} />}
                    </View>
                  )
                })}
              </View>
            ))}
          </View>
        )}
      </Sheet>
      {picker && (
        <RiderPicker riders={riders} onClose={() => setPicker(null)}
          onSelect={(id) => { setGrid(prev => ({ ...prev, [`${picker.userId}_${picker.slot}`]: id })); setPicker(null) }} />
      )}
    </>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  cardRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  cardDesc: { fontSize: 12, marginTop: -8 },
  pickerWrap: { flex: 1, borderWidth: 1, borderRadius: 8, overflow: 'hidden', height: 44 },
  pickerItem: { paddingHorizontal: 12, minHeight: 44, justifyContent: 'center', borderRadius: 6 },
  pickerItemText: { fontFamily: fonts.condensedBold, fontSize: 12 },
  stageBtn: { borderWidth: 1, borderRadius: 6, paddingHorizontal: 12, minHeight: 44, minWidth: 44, justifyContent: 'center', alignItems: 'center' },
  stageBtnText: { fontFamily: fonts.condensedBold, fontSize: 12 },
  adminUserCard: { borderRadius: 10, padding: 12, paddingHorizontal: 14, borderWidth: 1 },
  adminUserName: { fontSize: 12, color: tc.rosaText, fontFamily: fonts.condensedBold, textTransform: 'uppercase', marginBottom: 8 },
  adminSlotRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  adminSlotNum: { fontSize: 12, color: tc.rosaText, width: 16, fontFamily: fonts.condensedBold },
  slotBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, borderRadius: 8, borderWidth: 1, paddingHorizontal: 10, minHeight: 44 },
})
